import * as React from "react";

/**
 * Shared drag state for the dnd-kit migration.
 *
 * The single `DndContext` lives in `WidgetEditor`, but the field/container nodes
 * that need to react to the live drag (draw the drop-line, highlight the target
 * container) render deep inside the canvas templates. Rather than thread props
 * through every template, `WidgetEditor` publishes the current drag state here
 * and the field nodes in `dnd.tsx` consume it. Mirrors the uxp TreeView pattern
 * (drop-line driven by `overId` + `dropPosition`), see
 * uxp `SortableTreeNode.tsx` / `TreeView.tsx`.
 */
export type WDDropPosition = 'before' | 'after' | null;

export interface WDDragState {
    /** dnd-kit id of the field currently being dragged (null when idle). */
    activeId: string | null;
    /** id of the field the pointer is over (its own drop target). */
    overId: string | null;
    /** id of the container the pointer is over (for the drop-target highlight). */
    overContainerId: string | null;
    /** where the field will land relative to `overId` (before/after). */
    dropPosition: WDDropPosition;
}

export const WD_DRAG_IDLE: WDDragState = {
    activeId: null,
    overId: null,
    overContainerId: null,
    dropPosition: null,
};

export const WDDragStateContext = React.createContext<WDDragState>(WD_DRAG_IDLE);

export function useWDDragState(): WDDragState {
    return React.useContext(WDDragStateContext);
}
